// EvalLog: streaming nix-eval logs rendered in CodeMirror with ANSI coloring.
//
// Shape mirrors BuildLog deliberately — same look-and-feel ("stream
// like our build log, and be in color") — but operates on a different
// push-event channel (eval_log_line / eval_log_clear) and a different
// custom-element tag so the two panels can coexist on the pipeline
// page during the evaluating-and-jobs-are-already-running window.
//
// Reuses the existing CodeMirror-based viewer helpers from
// `assets/ts/codemirror.ts` so the ANSI parser, the auto-scroll
// behaviour, and the line-batched render path are identical to the
// build-log experience. No second viewer impl is loaded.

import type { LiveViewHook, CiPushEventWire } from "../types";
import {
  initBuildLogViewer,
  appendBuildLogText,
  setBuildLogContent,
  scrollBuildLogToEnd,
  destroyBuildLogViewer,
  isBuildLogAutoScroll,
} from "../../codemirror";

type EvalLogHook = LiveViewHook<CiPushEventWire> & {
  _pendingLines: string[];
  _rafId: number;
};

const EVENT_LINE = "evalLogLine" as const;
const EVENT_CLEAR = "evalLogClear" as const;

export const EvalLog = {
  mounted(this: EvalLogHook) {
    initBuildLogViewer(this.el);

    this._pendingLines = [];
    this._rafId = 0;

    const flush = (): void => {
      const lines = this._pendingLines;
      if (lines.length === 0) return;
      this._pendingLines = [];
      // Lines arrive without their terminator (Runner.Eval.pumpStderr
      // strips the LF before the runner sends each one as a binary
      // eval-log frame); add it back here so consecutive lines
      // visually separate inside the CodeMirror buffer.
      appendBuildLogText(this.el, lines.join("\n") + "\n");
      if (isBuildLogAutoScroll(this.el)) scrollBuildLogToEnd(this.el);
    };

    this.handleEvent(EVENT_LINE, (raw: unknown) => {
      const { lines } = raw as { lines: string[] };
      for (const line of lines) this._pendingLines.push(line);
      if (!this._rafId) {
        this._rafId = requestAnimationFrame(() => {
          this._rafId = 0;
          flush();
        });
      }
    });

    this.handleEvent(EVENT_CLEAR, () => {
      setBuildLogContent(this.el, "");
      this._pendingLines = [];
    });
  },

  destroyed(this: EvalLogHook) {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    destroyBuildLogViewer(this.el);
  },
};
